import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IoArrowBackOutline } from "react-icons/io5";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import {
  addQuestion,
  updateQuestion,
  fetchQuestionById,
  translateQuestion,
  clearSelectedQuestion,
} from "../../../store/reducers/questionsSectionSlice";
import { toast } from "react-toastify";

const QuestionAddUpdate = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const questionId = params.get("id");

  const dispatch = useDispatch();
  const { selectedQuestion } = useSelector((state) => state.questions);

  const today = new Date().toISOString().split("T")[0];

  const [showDanish, setShowDanish] = useState(false);
  const [translateLoading, setTranslateLoading] = useState(false);

  const [formData, setFormData] = useState({
    date: today,
    version: "v1",
    active: false,
    question_en: "",
    question_dk: "",
    letter: "",
    tag: "",
    options: [
      { text_en: "", text_dk: "", correct: false },
      { text_en: "", text_dk: "", correct: false },
    ],
  });
  const [errors, setErrors] = useState({});

  /* ---------------- FETCH QUESTION ---------------- */

  useEffect(() => {
    if (questionId) {
      dispatch(fetchQuestionById(questionId));
    }

    return () => dispatch(clearSelectedQuestion());
  }, [dispatch, questionId]);

  /* ---------------- SET DATA FOR EDIT ---------------- */
  useEffect(() => {
    if (selectedQuestion) {
      setFormData({
        date: selectedQuestion.date_added || today,
        version: selectedQuestion.version || "v1",
        active: selectedQuestion.active || false,
        question_en: selectedQuestion.question || "",
        question_dk: selectedQuestion.question_dk || "",
        options: selectedQuestion.options || [],
        tag: selectedQuestion.tag || "",
        letter: selectedQuestion.letter || "",
      });

      setShowDanish(!!selectedQuestion.question_dk);
    }
  }, [selectedQuestion]);
  /* ---------------- HANDLE CHANGE ---------------- */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ---------------- OPTIONS ---------------- */

  const handleOptionChange = (index, field, value) => {
    const updated = [...formData.options];
    updated[index][field] = value;

    setFormData({ ...formData, options: updated });
  };

  const handleCorrectAnswer = (index) => {
    const updated = formData.options.map((opt, i) =>
      i === index ? { ...opt, correct: !opt.correct } : opt
    );
  
    setFormData((prev) => ({
      ...prev,
      options: updated,
    }));
  
    setErrors((prev) => ({ ...prev, correct: "" }));
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [
        ...formData.options,
        { text_en: "", text_dk: "", correct: false },
      ],
    });
  };

  const removeOption = (index) => {
    const updated = formData.options.filter((_, i) => i !== index);

    setFormData({ ...formData, options: updated });
  };

  /* ---------------- TRANSLATE ---------------- */
  const handleTranslate = async () => {
    try {
      setTranslateLoading(true);

      const payload = {
        question: formData.question_en,
        options: formData.options.map((opt, i) => ({
          id: i + 1,
          text_en: opt.text_en,
          text_dk: "",
          correct: opt.correct,
        })),
      };

      const resultAction = await dispatch(translateQuestion(payload));

      if (translateQuestion.fulfilled.match(resultAction)) {
        const res = resultAction.payload;

        const translatedOptions = res.options.map((opt, i) => ({
          ...formData.options[i],
          text_dk: opt.text_dk,
        }));

        setFormData((prev) => ({
          ...prev,
          question_dk: res.question_dk,
          options: translatedOptions,
        }));

        setShowDanish(true);
      } else {
        throw new Error();
      }
    } catch {
      toast.error("Translation failed");
    } finally {
      setTranslateLoading(false);
    }
  };

  /* ---------------- VALIDATION ---------------- */

  const validate = () => {
    let newErrors = {};

    if (!formData.question_en.trim()) {
      newErrors.question_en = "English question required";
    }

    if (showDanish && !formData.question_dk.trim()) {
      newErrors.question_dk = "Danish question required";
    }

    formData.options.forEach((opt, index) => {
      if (!opt.text_en.trim()) {
        newErrors[`option_en_${index}`] = "English option required";
      }

      if (showDanish && !opt.text_dk.trim()) {
        newErrors[`option_dk_${index}`] = "Danish option required";
      }
    });

    if (!formData.options.some((o) => o.correct)) {
      newErrors.correct = "At least one correct answer is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    if (!validate()) return;

    const correctCount = formData.options.filter(opt => opt.correct).length;
    try {
      const payload = {
        question: formData.question_en,
        question_dk: formData.question_dk,
        tag: formData.tag,
        letter: formData.letter,
        active: formData.active,
        version: formData.version,
        correct_answers:correctCount,
        options: formData.options.map((opt, i) => ({
          id: i + 1,
          text_en: opt.text_en,
          text_dk: opt.text_dk,
          correct: opt.correct,
        })),
      };

      if (questionId) {
        await dispatch(
          updateQuestion({
            id: questionId,
            data: payload,
          })
        ).unwrap();

        toast.success("Question updated successfully");
      } else {
        await dispatch(addQuestion(payload)).unwrap();

        toast.success("Question added successfully");
      }

      history.push("/admin-question");
    } catch {
      toast.error("Save failed");
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="container-fluid mt-3 overflow-auto">
      {/* HEADER */}
      <div className="d-flex align-items-center mb-3">
        <button
          className="btn btn-sm btn-outline-secondary me-3"
          onClick={() => history.push("/admin-question")}
        >
          <IoArrowBackOutline /> Back
        </button>

        <h5>{questionId ? "Update Question" : "Add Question"}</h5>
      </div>

      <hr />
      {/* DATE / VERSION / ACTIVE */}
      <div className="row mb-3 align-items-center">
        <div className="col-md-4">
          <label>Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.date}
            disabled
          />
        </div>

        <div className="col-md-4">
          <label>Version</label>
          <input
            type="text"
            name="version"
            className="form-control"
            value={formData.version}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4 d-flex align-items-center">
          <label className="form-label mb-0 me-5">Active</label>

          <div className="form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              name="active"
              checked={formData.active}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label">Letter</label>
          <input
            type="text"
            className="form-control"
            name="letter"
            value={formData.letter}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Tag</label>
          <input
            type="text"
            className="form-control"
            name="tag"
            value={formData.tag}
            onChange={handleChange}
          />
        </div>
      </div>

      <hr />
      {/* ENGLISH QUESTION */}
      <div className="mb-3">
        <label>English Question*</label>

        <textarea
          rows="3"
          name="question_en"
          value={formData.question_en}
          onChange={handleChange}
          className={`form-control ${errors.question_en ? "is-invalid" : ""}`}
        />

        {errors.question_en && (
          <div className="invalid-feedback">{errors.question_en}</div>
        )}
      </div>

      {/* DANISH QUESTION */}
      {(showDanish || formData.question_dk) && (
        <div className="mb-3">
          <label>Danish Question</label>

          <textarea
            rows="3"
            name="question_dk"
            value={formData.question_dk}
            onChange={handleChange}
            className={`form-control ${errors.question_dk ? "is-invalid" : ""}`}
          />

          {errors.question_dk && (
            <div className="invalid-feedback">{errors.question_dk}</div>
          )}
        </div>
      )}

      <hr />
      {/* OPTIONS HEADER */}
      <div className="d-flex justify-content-between mb-2">
        <h6>Options</h6>

        <button className="btn btn-success btn-sm" onClick={addOption}>
          <FiPlus /> Add Option
        </button>
      </div>

      <div className="row fw-bold mb-2">
        <div className="col-md-2">Correct Answer</div>
        <div className="col-md-4">English Option</div>
        {(showDanish || formData.question_dk) && (
          <div className="col-md-4">Danish Option</div>
        )}
        <div className="col-md-1"></div>
      </div>

      {formData.options.map((opt, index) => (
        <div className="row mb-2 align-items-start" key={index}>
          <div className="col-md-2 text-center pt-2">
            <input
              type="checkbox"
              checked={opt.correct}
              onChange={() => handleCorrectAnswer(index)}
            />
          </div>

          {/* ENGLISH OPTION */}
          <div className="col-md-4">
            <input
              className={`form-control ${
                errors[`option_en_${index}`] ? "is-invalid" : ""
              }`}
              value={opt.text_en}
              onChange={(e) =>
                handleOptionChange(index, "text_en", e.target.value)
              }
            />

            {errors[`option_en_${index}`] && (
              <div className="invalid-feedback">
                {errors[`option_en_${index}`]}
              </div>
            )}
          </div>

          {/* DANISH OPTION */}
          {(showDanish || formData.question_dk) && (
            <div className="col-md-4">
              <input
                className={`form-control ${
                  errors[`option_dk_${index}`] ? "is-invalid" : ""
                }`}
                value={opt.text_dk}
                onChange={(e) =>
                  handleOptionChange(index, "text_dk", e.target.value)
                }
              />

              {errors[`option_dk_${index}`] && (
                <div className="invalid-feedback">
                  {errors[`option_dk_${index}`]}
                </div>
              )}
            </div>
          )}

          <div className="col-md-1 text-end">
            {formData.options.length > 2 && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => removeOption(index)}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        </div>
      ))}

      {errors.correct && (
        <div className="text-danger mt-2">{errors.correct}</div>
      )}

      <hr />
      <div className="mt-4 mb-2">
        <button
          className="btn px-2 mr-2"
          style={{
            border: "1px solid red",
            color: "red",
            background: "transparent",
          }}
          onClick={handleTranslate}
          disabled={!formData.question_en.trim() || translateLoading}
        >
          {translateLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Translating...
            </>
          ) : (
            "Translate to Danish"
          )}
        </button>

        <button className="btn px-4 bg_color" onClick={handleSubmit} disabled={translateLoading}>
          {questionId ? "Update Question" : "Add Question"}
        </button>
      </div>
    </div>
  );
};

export default QuestionAddUpdate;
